/**
 * Upload primitives for arcano.app
 * This code is under copyright Arcano Gmbh (https://arcano.app).
 *
 * - no commercial use
 * - no modifications
 * - no redistribution
 *
 * without written consent of Arcano Gmbh, zentralstrasse 47, 8003 zürich, switzerland, https://arcano.app
 */

'use strict';

import {
    convertStringToArrayBufferView,
    encrypt,
    encryptMessage,
    getRsaAlgorithm,
    getSymmetricAlgorithm,
    randomHash,
    prepareFileSharing,
} from "./crypto";
import {
    escapeHtml,
    disableWarningOnPageUnload,
    enableWarningOnPageUnload,
    getUppyFrenchLocale,
    getUppyGermanLocale,
    mfetch,
} from "./utils";

// @ts-ignore
import Uppy, {Plugin, UploadResult, UppyFile} from '@uppy/core'
// @ts-ignore
import AwsS3Multipart from '@uppy/aws-s3-multipart'
// @ts-ignore
import Dashboard from '@uppy/dashboard'
// @ts-ignore

// import Audio from "@uppy/audio";
// import Webcam from "@uppy/webcam";

import {updateTable} from "./download";
import {translate, localizeSecret} from "./translations";


// @ts-ignore
const uppy: Uppy.Uppy = new Uppy();

export function getUppy(){
    return uppy
}

interface ArcanoS3Multipart extends Plugin{
    crypto?: {
        prefix: string;
        counter: Uint8Array;
        key: CryptoKey;
        algorithm: {
            name: string;
            length: number;
            iv: Uint8Array
        }
    };
}


export interface FileData extends UppyFile{
    to_raw: any;
    from_raw: string;
    from_uid: string;
    size: number;
    done?: boolean,
    b64encryptedMessage?: ArrayBuffer | string;
    encryptedMessage?: ArrayBuffer | string;
    id: string,
    name: string,
    hashedFilename?: string
    meta: any,
    fileID: string,
    url: string,
    nChunks: number,
    b64encryptedFilename?: ArrayBuffer | string,
    b64encryptedRelativePath?: ArrayBuffer | string,
    data: any,
    nUploads?: number,
    prefix: string,
    create_date: number,
    from: string,
    filename: string|null,
    downloads: string,
    download_count: number,
    encrypted_message: string,
    encryptedkey: string,
    user_verified: string,
    verified: string,
    expiry: string
    file_id: string,
    real_size: number,
    actions: string,
    date: string,
    encrypted_filename: string,
    encrypted_filepath: string,
    encrypted_transfername: string,
    password_hash: string,
    message: string|null,
    transfer_name: string|null
}


function disableUIUploadInputs() {

    const fields = [
        'message-input',
        'transfer-name-input',
        'expiry-days-input',
        'expiry-download-input',
        'passphrase-input',
        // 'sender-name',
        'sender-input'
    ]

    for(let i=0; i< fields.length; i++) {
        const elem = document.getElementById(fields[i]);
        if(elem) {
            elem.setAttribute('disabled', '1')
        }
    }
}

function enableUIUploadInputs() {

    const fields = [
        'message-input',
        'transfer-name-input',
        'expiry-days-input',
        'expiry-download-input',
        'passphrase-input',
        // 'sender-name',
        'sender-input'
    ]

    for(let i=0; i< fields.length; i++) {
        const elem = document.getElementById(fields[i]);
        if(elem) {
            elem.removeAttribute('disabled')
        }
    }
}


/**
 * updates the UI if upload is successful
 */
function updateUISuccessUpload(isExternal: boolean) {

    const shareLink = document.getElementById('uppy-share-link');
    if(shareLink) {
        if (isExternal) {
            if (shareLink) {
                shareLink.style.display = 'none';
            }
        } else {
            // $('#copyLinkModal').modal();
            shareLink.style.display = 'block';
        }
    }
    document.getElementById('upload-options-col')!.style.display = 'none';

    document.getElementById('result')!.style.display = 'block';
    const uppyAddMoreButton = document.querySelector('.uppy-DashboardContent-addMore') as HTMLElement;
    if(uppyAddMoreButton) {
        uppyAddMoreButton.style.display = 'none'
    }
}

/**
 * Reset then upload UI
 */
function updateUIReset() {
    document.getElementById('result')!.style.display = 'none';
    const uppyAddMoreButton = document.querySelector('.uppy-DashboardContent-addMore') as HTMLElement;
    if(uppyAddMoreButton) {
        uppyAddMoreButton.style.display = 'block'
    }
}


/**
 * Fetches the public key given a user UID
 */
function fetchPublicKey(userUid: string): Promise<ArrayBuffer> {
    return fetch('/key/' + userUid).then(response => response.arrayBuffer());
}

/**
 * Fetches the file prefix for this upload
 */
async function fetchFilesPrefix(url: string,
                                senderName: string,
                                senderEmail: string,
                                passphrase: string,
                                message: string,
                                downloadLimit: string,
                                daysToLive: string,
                                files: Array<FileData>,
                                key: CryptoKey,
                                algorithm: Algorithm,
                                transferName?: string): Promise<[number, string]>  {

    const data = [];
    for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
        const hashedFilename = randomHash();
        files[fileIndex].hashedFilename = hashedFilename
        const encryptedFilename = await encryptMessage(new TextEncoder().encode(files[fileIndex].name), key, algorithm);
        //encryptedFilenamelet arr = Array.from(new Uint8Array(encryptedTransferName))
        const b64encryptedFilename = btoa(String.fromCharCode(...Array.from(new Uint8Array(encryptedFilename))));
        files[fileIndex].b64encryptedFilename = b64encryptedFilename;

        let b64encryptedRelativePath = '';
        if (files[fileIndex].meta.relativePath) {
            let encryptedRelativePath = await encryptMessage(new TextEncoder().encode(files[fileIndex].meta.relativePath), key, algorithm);
            b64encryptedRelativePath = btoa(String.fromCharCode(...Array.from(new Uint8Array(encryptedRelativePath))));
        } else if (files[fileIndex].data.webkitRelativePath) {
            let encryptedRelativePath = await encryptMessage(new TextEncoder().encode(files[fileIndex].data.webkitRelativePath), key, algorithm);
            b64encryptedRelativePath = btoa(String.fromCharCode(...Array.from(new Uint8Array(encryptedRelativePath))));
        }
        files[fileIndex].b64encryptedRelativePath = b64encryptedRelativePath;

        let b64encryptedMessage = '';
        let encryptedMessage: ArrayBuffer | string = ''
        if (message !== '') {
            encryptedMessage = await encryptMessage(new TextEncoder().encode(message), key, algorithm);
            b64encryptedMessage = btoa(String.fromCharCode(...Array.from(new Uint8Array(encryptedMessage))));

        }
        let b64encryptedTransfername = '';
        let encryptedTransfername: ArrayBuffer | string = ''
        if (transferName !== '') {
            encryptedTransfername = await encryptMessage(new TextEncoder().encode(transferName), key, algorithm);
            b64encryptedTransfername = btoa(String.fromCharCode(...Array.from(new Uint8Array(encryptedTransfername))));
        }

        files[fileIndex].encryptedMessage = encryptedMessage;
        files[fileIndex].b64encryptedMessage = b64encryptedMessage;

        data[fileIndex] = {
            'name': hashedFilename,
            'size': files[fileIndex].size,
            'senderName': senderName,
            'senderEmail': senderEmail,
            'passphrase': passphrase,
            'download_limit': downloadLimit,
            'days_to_live': daysToLive,
            'encrypted_filename': b64encryptedFilename,
            'encrypted_filepath': b64encryptedRelativePath,
            'encrypted_message': b64encryptedMessage,
            'encrypted_transfername': b64encryptedTransfername
        };
    }


    return await fetch(url, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
    }).catch((err) => {
            throw err
    }).then(async (response) => {
        if (response.status === 402){
            console.log(`No more transfers remaining`, "warning");
            uppy.info({
                message: 'No more transfers remaining!',
                details: 'This user has no more transfers in his subscription',
            }, 'error', 15000)
        }
        else if(response.status === 404){
            return [404, "not-found"]
        }
        const prefix = await response.text()
        return [response.status, prefix];
    });




}

/**
 * Get the form addons
 */
function getUploadOptions(): {
    recipientEmail?:string,
    senderName?: string,
    senderEmail?: string,
    userUid?: string,
    passphrase?: string,
    downloadLimit?: string,
    daysToLive?: string,
    message?: string,
    transferName?: string
} {

    let recipientEmail, senderName, senderEmail, userUid, passphrase, downloadLimit, daysToLive, message, transferName;

        // let senderNameElement = document.getElementById('sender-name') as HTMLInputElement;
    // if (senderNameElement) {
    //     senderName = escapeHtml(senderNameElement.value);
    // }

    let recipientMailelement = document.getElementById('recipient-input') as HTMLInputElement;
    if (recipientMailelement && recipientMailelement.disabled !== true) {
        recipientEmail = escapeHtml(recipientMailelement.value);
    }

    let senderEmailElement = document.getElementById('sender-input') as HTMLInputElement;
    if (senderEmailElement) {
        senderEmail = escapeHtml(senderEmailElement.value);
    }

    let userUidElement = document.getElementById('sender-uid') as HTMLInputElement;
    if (userUidElement) {
        userUid = escapeHtml(userUidElement.value);
    }

    let passphraseElement = document.getElementById('passphrase-input') as HTMLInputElement;
    if (passphraseElement) {
        passphrase = escapeHtml(passphraseElement.value);
    }

    let downloadLimitElement = document.getElementById('expiry-download-input') as HTMLInputElement;
    if (downloadLimitElement) {
        downloadLimit = escapeHtml(downloadLimitElement.value);
    }

    let daysToLiveElement = document.getElementById('expiry-days-input') as HTMLInputElement;
    if (daysToLiveElement) {
        daysToLive = escapeHtml(daysToLiveElement.value);
    }

    let messageElement = document.getElementById('message-input') as HTMLInputElement;
    if (messageElement) {
        message = escapeHtml(messageElement.value);
    }

    let transferNameElement = document.getElementById('transfer-name-input') as HTMLInputElement;
    if (transferNameElement) {
        transferName = escapeHtml(transferNameElement.value);
    }

    return {recipientEmail, senderName ,senderEmail, userUid, passphrase, downloadLimit, daysToLive, message, transferName};
}



/**
 * When I upload via an external download to someone, I'd like to have this file
 * In my sent box
 */
async function shareWithMyself(prefix: string, secret: string, key: CryptoKey, senderUid: string) {


    const files = await fetch("/files/list/prefix/" + prefix).then(response => response.json())

    //share with myself
    const binary = await fetchPublicKey(senderUid);

    const userData = JSON.parse(String.fromCharCode.apply(null, Array.from(new Uint8Array(binary))));
    const publicKeyJwk = JSON.parse(userData.publicKey);
    const rsaAlgo = getRsaAlgorithm();

    const publicKey = await window.crypto.subtle.importKey('jwk',
        publicKeyJwk,
        rsaAlgo,
        false,
        publicKeyJwk.key_ops);

    const sharedFiles = await prepareFileSharing(files, secret, userData, key, publicKey);

    mfetch('/files/share', {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({files: sharedFiles, sender_uid: senderUid, receiver_uid: senderUid, direction: 'out'}),
    }).then((response) => {
        if (response.status === 200) {
            console.log('OK')
        } else {
            console.log('FAILED TO SHARE TODO') // Todo
        }
    })
}

export function isUserView(): HTMLElement|undefined {
    const tableMode = document.getElementById('table_mode') as HTMLInputElement;

    const isUserView = !(tableMode.value === 'inbox' || tableMode.value === 'drive' || tableMode.value === 'outbox' || tableMode.value === 'external_download' || tableMode.value === '');

    let contact;  // undefined
    if (isUserView) {
        contact = document.querySelector('.sidebar-button.active') as HTMLElement
    }
    return contact
}
function uploadSecret(encryptedKey: ArrayBuffer,
                      prefix: string,
                      userUid: string,
                      isExternalUpload: boolean,
                      secret: string,
                      key: CryptoKey) {
    let request = new XMLHttpRequest();
    request.open('POST', '/upload_encrypted_key/' + userUid + '/' + prefix, true);
    request.onreadystatechange = async function () {
        // Only run if the request is complete
        // noinspection NonBlockStatementBodyJS
        if (request.readyState !== 4) return;

        // Process the response
        // noinspection MagicNumberJS
        if (request.status >= 200 && request.status < 300) {
            // localize secret
            const localizedSecretLink = localizeSecret(secret)


            // If successful
            console.log('Success'); // check if properly handled by uppy signals (emit)

            let isUpload = false;
            if (window.location.pathname.includes('/upload')) {
                isUpload = true;
            }

            updateUISuccessUpload(isExternalUpload || isUpload);

            updateTable();

            let subject = document.getElementById('email_subject') as HTMLInputElement;
            let body = document.getElementById('email_body') as HTMLInputElement;
            let footer = document.getElementById('email_footer') as HTMLInputElement;
            if (subject !== null && body !== null) {

                const mail_subject = encodeURIComponent(subject.value);
                let mail_secret = encodeURIComponent(body.value.replace('\n', '\r\n'))  + '%0D%0A' + encodeURIComponent(localizedSecretLink);
                const e = document.getElementById('expiry-days-input') as HTMLSelectElement;
                const text = e. options[e. selectedIndex]. text;
                mail_secret += '%0D%0A%0D%0A' + encodeURIComponent(footer.value.replace('\n', '\r\n')) + encodeURIComponent(text)
                mail_secret += '%0D%0A%0D%0A' + encodeURIComponent('ARCANO.\nStay safe') + '%0D%0A' + encodeURIComponent('https://arcano.app');
                const shareLink = document.getElementById('secret-mailer') as HTMLAnchorElement;
                shareLink.href = "mailto:?subject=" + mail_subject + "&body=" + mail_secret;

                const removeAnimation = () => {
                    document.getElementById('uppy-share-link')!.style.animation = 'unset';
                    shareLink.removeEventListener('click', removeAnimation);
                }
                shareLink.addEventListener('click', removeAnimation);
            }

            const clipBoard = document.getElementById('clipboardCopySecret') as HTMLInputElement
            if (clipBoard) {
                clipBoard.value = localizedSecretLink;
            }

            const pluginLink = document.getElementById('plugin-copy-link') as HTMLAnchorElement;
            if(pluginLink) {
                pluginLink.href = localizedSecretLink;
            }

            // at the very last
            const senderUidElement = document.getElementById('sender-uid') as HTMLInputElement;
            const senderUid = senderUidElement && senderUidElement.value;
            // external upload and I'm logged in
            if (senderUid &&
                (document.location.href.indexOf('/external_upload') >= 0 || document.location.href.indexOf('/upload') >= 0)) { // user is logged in
                shareWithMyself(prefix, secret, key, senderUid).then(() => {})
            }

            const tableMode = document.getElementById('table_mode') as HTMLInputElement;
            if (!tableMode) {
                return
            }
            const receiverInput = document.getElementById('recipient-input') as HTMLInputElement
            if(receiverInput && receiverInput.value !== '') {
                const contact = isUserView()
                if (contact) {
                    shareWithMyself(prefix, secret, key, senderUid).then(() => {
                        const dashboard = uppy.getPlugin("Dashboard") as Dashboard;
                        uppy.cancelAll()
                        dashboard.closeModal()
                        document.getElementById('result')!.style.display = 'none';
                        // console.log('clickiong');
                        setTimeout(() => {
                            (document.querySelector('.sidebar-button.active')! as HTMLElement).click()
                        }, 500)
                    })
                }
            }

            const uploadsAlreadyDone = document.getElementById('remaining-uploads-indicator') as HTMLSpanElement; // 0,1,2,3,...
            if(uploadsAlreadyDone) {

                const contact = isUserView()
                const otherHasNoSubscription = contact && !Boolean(contact.dataset.subscription)

                const remainingUploadsTotal = document.getElementById('remaining-uploads-total') as HTMLElement;  // 3 or 5 or nothing if subscribed
                if (remainingUploadsTotal && otherHasNoSubscription){
                    const uploads = parseInt(uploadsAlreadyDone.innerText) + 1;
                    uploadsAlreadyDone.innerText = uploads.toString();

                    const remainingUploadsProgressbar = document.getElementById('remaining-uploads-progressbar') as HTMLElement;

                    remainingUploadsProgressbar.style.width = ((100 * uploads) / parseInt(remainingUploadsTotal.innerText)).toString()  + '%';

                    const remainingUploads = document.getElementById('sender-remaining-uploads') as HTMLInputElement; // 1,2,3,4,5
                    remainingUploads.value = (parseInt(remainingUploadsTotal.innerText) -  parseInt(uploadsAlreadyDone.innerText)).toString();

                    const showGoodie = document.getElementById('show-goodie-div') as HTMLDivElement;
                    if(showGoodie)
                    {
                        if(remainingUploads.value === '0') {
                            showGoodie.style.display = 'block';
                        }
                    }
                }


            }
        } else {
            // If failed
            console.log('Failed'); // check if properly handled by uppy signals (emit)

        }
    };
    request.send(new DataView(encryptedKey));  // this conversion d
}
async function encryptSecretWithPublicKey(userUid: string,
                                          secret: string,
                                          prefix: string,
                                          isExternalUpload: boolean,
                                          key: CryptoKey) {

    const binary = await fetchPublicKey(userUid);
    const userData = JSON.parse(String.fromCharCode.apply(null, Array.from(new Uint8Array(binary))));
    const publicKeyJSON = JSON.parse(userData.publicKey);
    const rsaAlgo = getRsaAlgorithm();

    const publicKey = await window.crypto.subtle.importKey('jwk',
        publicKeyJSON,
        rsaAlgo,
        false,
        publicKeyJSON.key_ops)

    ///
    /*
    * - Check if we entered a passphrase or mobile
    * - If passphrase or mobile
    *    - const {password1, password2, secret1, secret2} = await shareSecret(key);
    *    - upload password2 to server..... (if we want it to be recoverable)
    *    - upload secrtets to server
    *
    *
    *
    */

    ///
    try {
        const encryptedKey = await window.crypto.subtle.encrypt({
                name: "RSA-OAEP",
                iv: userData.iv,
            },
            publicKey, //from generateKey or importKey above
            convertStringToArrayBufferView(secret)
        )
        uploadSecret(encryptedKey, prefix, userUid, isExternalUpload, secret, key)
    } catch (e) {
        console.log(e)
    }
}


async function prepareEncryptionKeysForUpload(uppy: Uppy) {
    const counter = window.crypto.getRandomValues(new Uint8Array(16));
    const algorithm = getSymmetricAlgorithm(counter);
    const searchParams = new URLSearchParams(window.location.search.slice(1));
    console.log('generating keys')

    const key = await window.crypto.subtle.generateKey(
        algorithm,
        true,
        ["encrypt", "decrypt"]
    );

    let {senderName, senderEmail, passphrase, downloadLimit, daysToLive, message, transferName} = getUploadOptions();
    let prefix_url = '/file_prefix';

    if (searchParams.has("public")) {
        prefix_url = '/file_prefix/' + searchParams.get("public");
    }
    
    const contact = isUserView()
    if(contact) {
        prefix_url = '/file_prefix/' + contact.dataset.uid;
    }

    const files = uppy.getFiles()
    const [status, prefix] = await fetchFilesPrefix(prefix_url,
                                                    senderName!,
                                                    senderEmail!,
                                                    passphrase!,
                                                    message!,
                                                    downloadLimit!,
                                                    daysToLive!,
                                                    (files as Array<FileData>),
                                                    key,
                                                    algorithm,
                                                    transferName);
    const uploader = uppy.getPlugin("AwsS3Multipart") as ArcanoS3Multipart;
    if (status === 200) {
        uploader.crypto = {
            key: key,
            counter: counter,
            algorithm: algorithm,
            prefix: prefix
        }
    } else {
        delete uploader.crypto
    }
}

function cleanUIInputs() {
    let passPhraseInput = document.getElementById('passphrase-input') as HTMLInputElement;
    if(passPhraseInput) {
        passPhraseInput.value = '';
    }
    let expiryDays = document.getElementById('expiry-days-input') as HTMLInputElement;
    if(expiryDays) {
        const remainingUploads = document.getElementById('sender-remaining-uploads') as HTMLInputElement;
        if(parseInt(remainingUploads.value) <=5){
            expiryDays.value = '2';
        }
        else{
            expiryDays.value = '7';
        }

    }
    let expiryDownloads = document.getElementById('expiry-download-input') as HTMLInputElement;
    if(expiryDownloads) {
        expiryDownloads.value = '10';
    }
    let message = document.getElementById('message-input') as HTMLInputElement;
    if(message) {
        message.value = '';
    }
    let transferName = document.getElementById('transfer-name-input') as HTMLInputElement;
    if(transferName) {
        transferName.value = '';
    }
}


function enableUploadButton(uppy: Uppy) {

    const uploadButton = document.getElementById('upload-button');
    if(!uploadButton) {
        return;
    }

    const uploadCol = document.getElementById('upload-options-col');
    if(!uploadCol) {
        return;
    }

    const hasFiles = uppy.getFiles().length > 0;
    const requiredFields = Array.from(uploadCol.querySelectorAll('[required]')) as Array<HTMLInputElement>;
    if(hasFiles) {
        requiredFields.map(requiredField => {
            if(!requiredField.validity.valid) {
                requiredField.classList.add('is-invalid');
            }
        })
    }

    const allInputsValid = requiredFields.every(requiredField => requiredField.validity.valid);  // note empty array returns true

    if(allInputsValid && hasFiles) {
        uploadButton.removeAttribute('disabled');
    } else {
        uploadButton.setAttribute('disabled', 'disabled');
    }
}

function customizeWidget(data: any, uppy: Uppy) {
    const isWidget = window.location.pathname.includes('/widget')

    if (isWidget && data.widget) {
        const showPin = data.widget.hasOwnProperty('showPinCheck');
        const passPhraseGroup = document.getElementById('passphrase-input-group');

        if(showPin) {
            passPhraseGroup!.parentElement!.parentElement!.style.display = 'block';
            if (data.widget.hasOwnProperty('widgetPinCheckRequired')) {
                const passPhrase = document.getElementById('passphrase-input') as HTMLInputElement;
                if (passPhrase) { // validate email
                    passPhrase.required = true;
                    passPhrase.placeholder = 'Required';
                    passPhrase.addEventListener('keyup', () => {
                        enableUploadButton(uppy)
                        if (passPhrase.validity.valid) {
                            passPhrase.classList.remove('is-invalid')
                        }
                    })
                }
            }
        } else {
            passPhraseGroup!.parentElement!.parentElement!.style.display = 'none';
        }


        if (data.widget.hasOwnProperty('showFromNameCheck')) {
            console.log('showFromNameCheck')
        } else {
            const transferName = document.getElementById('transfer-name-input');
            if (transferName) {
                transferName.parentElement!.parentElement!.parentElement!.style.display = 'none'
            }
        }
        const senderEmail = document.getElementById('sender-input') as HTMLInputElement;
        if (data.widget.hasOwnProperty('widgetEmailRequired')) {
            if (senderEmail) { // validate email
                senderEmail.required = true;
                senderEmail.addEventListener('keyup', () => {
                    enableUploadButton(uppy)
                    if (senderEmail.validity.valid) {
                        senderEmail.classList.remove('is-invalid')
                    }
                })
            }
        }


        enableUploadButton(uppy)
    }
}

function customizePersonalUpload(data: any, uppy: Uppy) {
    const isUpload = document.location.href.indexOf('/upload') >= 0;

    if (isUpload && data.upload) {
        if (data.upload.hasOwnProperty('showPinUpload')) {
            document.getElementById('passphrase-input-group')!.parentElement!.parentElement!.style.display = 'block';
            if (data.upload.hasOwnProperty('uploadPinCheckRequired')) {
                const passPhrase = document.getElementById('passphrase-input') as HTMLInputElement;
                if (passPhrase) { // validate email
                    passPhrase.required = true;
                    passPhrase.placeholder = 'Required';
                    passPhrase.addEventListener('keyup', () => {
                        enableUploadButton(uppy)
                        if (passPhrase.validity.valid) {
                            passPhrase.classList.remove('is-invalid')
                        }
                    })
                }
            }
        } else {
            document.getElementById('passphrase-input-group')!.parentElement!.parentElement!.style.display = 'none';
        }

        const transferName = document.getElementById('transfer-name-input');
        if (transferName) {
            if (data.upload.hasOwnProperty('uploadShowFromNameCheck')) {
                transferName.parentElement!.parentElement!.parentElement!.parentElement!.style.display = 'block';
            } else {
                transferName.parentElement!.parentElement!.parentElement!.parentElement!.style.display = 'none';
            }
        }

        const senderEmail = document.getElementById('sender-input') as HTMLInputElement;
        if (data.upload.hasOwnProperty('uploadEmailRequired')) {
            if (senderEmail) { // validate email
                senderEmail.required = true;
                senderEmail.addEventListener('keyup', () => {
                    enableUploadButton(uppy)
                    if (senderEmail.validity.valid) {
                        senderEmail.classList.remove('is-invalid')
                    }
                })
            }
        }
    }
}

function customizeIframe(data: any, uppy: Uppy) {
    const isIframe = window.location.pathname.includes('/widget_iframe')
    if (isIframe && data.iframe) {
        const showPin = data.iframe.hasOwnProperty('showPinIFrame');
        const passPhraseGroup = document.getElementById('passphrase-input-group');
        if(showPin) {
            passPhraseGroup!.parentElement!.parentElement!.style.display = 'block'
            if (data.iframe.hasOwnProperty('iframePinCheckRequired')) {
                const passPhrase = document.getElementById('passphrase-input') as HTMLInputElement;
                if (passPhrase) { // validate email
                    passPhrase.required = true;
                    passPhrase.placeholder = 'Required';
                    passPhrase.addEventListener('keyup', () => {
                        enableUploadButton(uppy)
                        if (passPhrase.validity.valid) {
                            passPhrase.classList.remove('is-invalid')
                        }
                    })
                }
            }
        } else {
            passPhraseGroup!.parentElement!.parentElement!.style.display = 'none'
        }


        const transferName = document.getElementById('transfer-name-input');
        if (transferName) {
            if (data.iframe.hasOwnProperty('iframeShowFromNameCheck')) {
                transferName.parentElement!.parentElement!.parentElement!.style.display = 'block';
            } else {
                transferName.parentElement!.parentElement!.parentElement!.style.display = 'none';
            }
        }
        const senderEmail = document.getElementById('sender-input') as HTMLInputElement;
        if (data.iframe.hasOwnProperty('iframeEmailRequired')) {
            if (senderEmail) { // validate email
                senderEmail.required = true;
                senderEmail.addEventListener('keyup', () => {
                    enableUploadButton(uppy)
                    if (senderEmail.validity.valid) {
                        senderEmail.classList.remove('is-invalid')
                    }
                })
            }
        }


        enableUploadButton(uppy)
    }
}

/**
 * This sets up the whole dropzone business using uppy.io
 */
export function setupDropzone() {

    const isVersion2 = document.getElementById('transfer-file-list') !== null;


    if (!(document.getElementById('upload-row') || document.getElementById('transfer-file-list'))) {
        return
    }
    let uppyHeight = 240;
    let isExternalUpload = false;
    if (window.location.pathname.includes('/external_upload')) {
        //set the options
        isExternalUpload = true;
        uppyHeight = 160
    }

    let isWidget = false;
    if (window.location.pathname.includes('/widget')) {
        //set the options
        isWidget = true;
        uppyHeight = 160
    }

    if (window.location.pathname.includes('/widget_iframe')) {
        //set the options
        uppyHeight = 240
    }

    // get customizations
    let params = new URLSearchParams(document.location.search)
    mfetch('/get_widget/' + params.get('public'))
            .then(response => response.json())
            .then((data) => {
                customizePersonalUpload(data, uppy);
                customizeWidget(data, uppy);
                customizeIframe(data, uppy);
            });


    if (window.location.pathname.includes('/upload')) {
        //set the options
        isExternalUpload = true;
        uppyHeight = 360
        document.getElementById('upload-row')!.style.maxWidth = 'calc(min(600px, 90vw))';
    }

    const maxTransferVolumeInput = document.getElementById('max_transfer_volume') as HTMLInputElement;
    const maxTransferFilesInput = document.getElementById('max_transfer_files') as HTMLInputElement;
    const maxTransferVolume = parseInt(maxTransferVolumeInput.value);
    const maxTransferFiles = parseInt(maxTransferFilesInput.value);


    let options = {
        locale: {strings: {}},
        autoProceed: false,
        debug: false,
        allowMultipleUploads: true,
        restrictions: {
            maxTotalFileSize: maxTransferVolume,
            maxNumberOfFiles: maxTransferFiles
        },
        onBeforeFileAdded: (currentFile: UppyFile, files: any) => {
            const remainingUploads = document.getElementById('sender-remaining-uploads') as HTMLInputElement;
            const urlParams = new URLSearchParams(document.location.search);
            const isPublicUpload = urlParams.get('public');

            let hasSubscription = false;
            const contact = isUserView()
            if(contact) {
                hasSubscription = Boolean(contact.dataset.subscription);
            }


            if(parseInt(remainingUploads.value) <= 0 && hasSubscription === false){
                if(isPublicUpload) {
                    uppy.info({
                        message: translate("Transfer-limit exceeded, please contact the website-host!"),
                        details: translate('This user has reached their Arcano Client transfer-limit. Please contact him to ensure your file can be transferred safely.'),
                    }, 'error', 50000)
                }
                else{
                    uppy.info({
                        message: translate('No more transfers!'),
                        details: translate('You have reached your monthly transfer-limit for Arcano Client, please subscribe to transfer as many files as you want.'),
                    }, 'error', 50000)}
                return false
            }

            // if(isPublicUpload) {
            //     return true;
            // }
            return true;
        },

    }

    options.locale=  {
        strings: {
            myDevice: "My Computer",
            dropPasteFiles: 'Add files or %{browseFiles}',
            dropPasteFolders: '%{browseFiles}',
            dropPasteBoth: '%{browseFiles}%{browseFolders}',  // Drop files here,
            dropPasteImportFiles: 'ttttAdd files, %{browseFiles}:',
            dropPasteImportFolders: 'OOAdd files here or %{browseFolders} or upload from:',
            dropPasteImportBoth: '%{browseFiles} %{browseFolders}',
            dropHint: 'Drop your files here',
            browseFiles: 'Add files here',
            uploading: 'Encrypting and uploading',
            browseFolders: 'or select a folder',

        }
    }



    if (window.location.pathname.includes('/de/')) {
        options.locale = getUppyGermanLocale();
    }
    if (window.location.pathname.includes('/fr/')) {
        options.locale = getUppyFrenchLocale();
    }

    uppy.setOptions(options);


    let note = document.getElementById('uppy-note-div')!.innerText;



    if(isVersion2) {

        uppy.use(Dashboard, {
            trigger: '#open-uppy-button',
            inline: false,
            target: '.UppyDragDrop',
            width: 320,
            animateOpenClose: false,
            onRequestCloseModal: () => {
                // this.closeModal() // todo: do not close until asked the user if he wants to stop, check if uploading....
                // console.log(this)
                uppy.cancelAll();
                const dashboard = uppy.getPlugin("Dashboard") as Dashboard;
                dashboard.closeModal()
            },
            doneButtonHandler: null,
            hideUploadButton: true,
            height: 600,
            maxWidth: 400,
            maxHeight: 400,
            id: 'Dashboard',
            fileManagerSelectionType: 'both',  // both
            metaFields: [],
            note: note,
            showProgressDetails: true,
            disableThumbnailGenerator: false,
            proudlyDisplayPoweredByUppy: false,

        });
        uppy.on('dashboard:modal-open', () => {
            uploadOptions.style.display = 'block';
        })
        uppy.on('dashboard:modal-closed', () => {
            uploadOptions.style.display = 'none';
        })

    } else {
        uppy.use(Dashboard, {
            target: '.UppyDragDrop',
            inline: true,
            hideUploadButton: true,
            height: uppyHeight,
            // width: 300,
            id: 'Dashboard',
            fileManagerSelectionType: 'folders',  // both
            metaFields: [],
            note: note,
            showProgressDetails: true,
            disableThumbnailGenerator: false,
            proudlyDisplayPoweredByUppy: false,
        });

        // uppy.use(DropTarget, {
        //     target: document.body
        // })
    }



    // @ts-ignore
    // uppy.use(Uppy.DropTarget, {
    //     target: document.body
    // })

    // // @ts-ignore  // todo
    // uppy.use(Uppy.GoldenRetriever, {serviceWorker: true})
    //
    // if ('serviceWorker' in navigator) {
    //   navigator.serviceWorker
    //     .register('/sw.js') // path to your bundled service worker with GoldenRetriever service worker
    //     .then((registration) => {
    //       console.log('ServiceWorker registration successful with scope: ', registration.scope)
    //     })
    //     .catch((error) => {
    //       console.log('Registration failed with ' + error)
    //     })
    // }
    // uppy.use(Audio, {target: Dashboard, showAudioSourceDropdown: true}); // Uppy.Dashboard
    // uppy.use(Webcam, {target: Dashboard}); // Uppy.Dashboard
    // uppy.use(ScreenCapture, {target: Dashboard}); // Uppy.Dashboard
    // uppy.use(GoogleDrive, {target: Dashboard, companionUrl: 'https://companion.uppy.io'});
    // uppy.use(Dropbox, {target: Dashboard, companionUrl: 'https://companion.uppy.io'});
    // uppy.use(OneDrive, {target: Dashboard, companionUrl: 'https://companion.uppy.io'});


    let uploadButton = document.getElementById('upload-button')!;
    const uploadOptions = document.getElementById('upload-options-col')!
    const uploadRow = document.getElementsByClassName('uppy-Dashboard-inner')[0] as HTMLElement
    // const recipientInput = document.getElementById('recipient-input') as HTMLInputElement;

    // if (recipientInput){
    //     let focus = -1;
    //     //This works
    //     recipientInput.addEventListener("input",() => {
    //         // @ts-ignore
    //         mfetch("/autocomplete/list/" + recipientInput.value).then((r)=>r.json()).then((Data) => {
    //             let list = Data['list'];
    //
    //         let val = recipientInput.value;
    //         closeAllLists();
    //         if (!val) {
    //             return false;
    //         }
    //         let a = document.createElement("div");
    //         a.setAttribute("id", recipientInput.id + "autocomplete-list");
    //         a.setAttribute("class", "autocomplete-items");
    //         // @ts-ignore
    //         recipientInput.parentNode.appendChild(a);
    //
    //         for (let i = 0; i < list.length; i++) {
    //             /*check if the item starts with the same letters as the text field value:*/
    //             if (list[i].substr(0, val.length).toUpperCase() == val.toUpperCase()) {
    //                 /*create a DIV element for each matching element:*/
    //                 let b = document.createElement("div");
    //                 /*make the matching letters bold:*/
    //                 b.innerHTML = "<strong>" + list[i].substr(0, val.length) + "</strong>";
    //                 b.innerHTML += list[i].substr(val.length);
    //                 /*insert a input field that will hold the current array item's value:*/
    //                 b.innerHTML += "<input type='hidden' value='" + list[i] + "'>";
    //                 /*execute a function when someone clicks on the item value (DIV element):*/
    //                 b.addEventListener("click", function (e) {
    //                     /*insert the value for the autocomplete text field:*/
    //                     recipientInput.value = this.getElementsByTagName("input")[0].value;
    //                     /*close the list of autocompleted values,
    //                     (or any other open lists of autocompleted values:*/
    //                     closeAllLists();
    //                 });
    //                 a.appendChild(b);
    //             }
    //         }
    //             //TODO Delay
    //         })
    //     })
    //
    //     //Todo This doesnt work
    //
    //     // @ts-ignore
    //     recipientInput.addEventListener("keydown", function(e: KeyboardEvent) {
    //         if(!document.getElementById(recipientInput.id + "autocomplete-list")){
    //           return false
    //           }
    //           // @ts-ignore
    //           let x = x.getElementsByTagName("div");
    //
    //           if (e.code == "ArrowDown") {
    //             /*If the arrow DOWN key is pressed,
    //             increase the currentFocus variable:*/
    //             focus++;
    //             /*and and make the current item more visible:*/
    //             addActive(x);
    //           } else if (e.code == "ArrowUp") { //up
    //             /*If the arrow UP key is pressed,
    //             decrease the currentFocus variable:*/
    //             focus--;
    //             /*and and make the current item more visible:*/
    //             addActive(x);
    //           } else if (e.code == "Enter") {
    //             /*If the ENTER key is pressed, prevent the form from being submitted,*/
    //             e.preventDefault();
    //             if (focus > -1) {
    //               /*and simulate a click on the "active" item:*/
    //               if (x){
    //                   // @ts-ignore
    //                   x[focus].click();}
    //             }
    //       }
    //
    //     });
    //     function closeAllLists(element?: Element) {
    //         /*close all autocomplete lists in the document,
    //         except the one passed as an argument:*/
    //         let x = document.getElementsByClassName("autocomplete-items");
    //         for (let i = 0; i < x.length; i++) {
    //             if ((!element || element != x[i]) && element != recipientInput) {
    //                 // @ts-ignore
    //                 x[i].parentNode.removeChild(x[i]);
    //             }
    //         }
    //     }
    //             // @ts-ignore
    //     function addActive(x: Array<Element>) {
    //         /*a function to classify an item as "active":*/
    //         if (!x) return false;
    //         /*start by removing the "active" class on all items:*/
    //         removeActive(x);
    //         if (focus >= x.length) focus = 0;
    //         if (focus < 0) focus = (x.length - 1);
    //         /*add class "autocomplete-active":*/
    //         x[focus].classList.add("autocomplete-active");
    //     }
    //
    //     function removeActive(x: Array<Element>) {
    //         /*a function to remove the "active" class from all autocomplete items:*/
    //         for (let i = 0; i < x.length; i++) {
    //             x[i].classList.remove("autocomplete-active");
    //         }
    //     }
    // }

    let lastFile = undefined;


    uppy.on('complete', async (e: UploadResult) => {

        if (e.successful.map(x => x.progress?.uploadComplete).every(x => x)) {  //
            const uploader = uppy.getPlugin("AwsS3Multipart") as ArcanoS3Multipart;
            const crypto = uploader.crypto!;
            const key = crypto.key;
            const nonce = crypto.counter;
            const prefix = crypto.prefix;

            try {
                const keydata = await window.crypto.subtle.exportKey("jwk", key);

                //returns the exported key data
                //todo change server address



                let secret = window.location.protocol + '//' + window.location.host + '/external_download?prefix=' + prefix + '#';

                secret += btoa(nonce.toString()) + '-' + btoa(JSON.stringify(keydata));

                // split the secret if second secret is set

                // const secondFactor = document.getElementById('2fa-secret-input') as HTMLInputElement
                // const password2 = secondFactor.value;
                //
                // const {password1, secret1, secret2} = await shareSecret(key, 16, password2)
                // console.log(password1, password2, secret1, secret2)
                // encrypt password2 with public key so we can restore it......
                const searchParams = new URLSearchParams(window.location.search.slice(1));

                let {userUid, recipientEmail} = getUploadOptions();

                if (searchParams.has("public")) {
                    userUid = searchParams.get("public") as string;  // get the public user id
                }
                const contact = isUserView()
                if(contact) {
                    userUid = contact.dataset.uid;
                }

                // if (recipientEmail){
                //     // @ts-ignore
                //     userUid = await mfetch('/get_uid_by_username/'+recipientEmail).then((response)=>{response.json()})
                //         // .then((data: Data)=> {
                //         // userUid = data })  // Fetch UID from usernameinput
                // }

                await encryptSecretWithPublicKey((userUid as string), secret, prefix, isExternalUpload, key)

            } catch(err) {
                // check if properly handled by uppy signals (emit)
                console.error(err);
            } finally {
                const uploader = uppy.getPlugin("AwsS3Multipart") as ArcanoS3Multipart;
                delete uploader.crypto  // next upload must be different!
            }

            disableWarningOnPageUnload()

            const uploadCount = document.getElementById('upload_count') as HTMLInputElement
            const transferVolume = document.getElementById('transfer_volume') as HTMLInputElement
            const counter = document.getElementById('upload-counter')

            if(uploadCount && transferVolume && counter) {
                let currentCount = parseInt(counter.innerText) + 1;
                counter.innerText = currentCount.toString();

                const percentage = document.getElementById('transfer-percentage')!
                let transferSize = e.successful.map(x => x.size).reduce((a, b) => {  return a + b; });
                let newUploaded = parseFloat(document.getElementById('transfer-percentage')!.innerText) + transferSize / 1024 / 1024 / 1024 * 100

                percentage.innerText = newUploaded.toFixed(1);
                // update inner counters too, so you cannot overflow the limits
                (document.getElementById('upload_count') as HTMLInputElement).value = currentCount.toString();
                (document.getElementById('transfer_volume') as HTMLInputElement).value = (parseFloat(transferVolume.value) + transferSize).toString();

            }

            if (window.location.pathname.includes('/upload')) {
                const uploadSuccessUploadPage = document.getElementById('success-upload-page')
                if (uploadSuccessUploadPage) {
                    uploadSuccessUploadPage.style.visibility = 'visible';
                }
            }
        }
        lastFile = true
    });

    const pluginChooser = document.getElementById('choose-arcano-files')

    uppy.on('files-added', async () => {
        if(!isWidget) {
            uploadOptions.style.display =' block'
        }
        if(pluginChooser) {
            pluginChooser.style.display = 'none'
        }
        if(uploadButton) {
            uploadButton.style.visibility = 'visible'
        }
        // if(isExternalUpload) {
        //     await prepareEncryptionKeysForUpload(uppy);
        // }
        uploadRow.style.borderBottomLeftRadius = '0px'
        uploadRow.style.borderBottomRightRadius = '0px'

        enableUploadButton(uppy)

        // if(uploadButton) {
        //     uploadButton.removeAttribute("disabled");
        // }

    })
    uppy.on('file-removed', () => {

        if (uppy.getFiles().length === 0) {
            const uploader = uppy.getPlugin("AwsS3Multipart") as ArcanoS3Multipart;
            delete uploader.crypto
            if(!isVersion2) {
                if (!isWidget && !isExternalUpload) {
                    uploadOptions.style.display = ' none'
                }
            }

            if(pluginChooser) {
                pluginChooser.style.display = 'block'
            }

            uploadRow.style.borderBottomLeftRadius = '5px'
            uploadRow.style.borderBottomRightRadius = '5px'

            // if (uploadButton) {
            //     uploadButton.setAttribute("disabled", '1');
            // }
            enableUploadButton(uppy)

        }
    })
    uppy.on('cancel-all', () => {
        const uploader = uppy.getPlugin("AwsS3Multipart") as ArcanoS3Multipart;
        delete uploader.crypto
        updateUIReset()
        enableUIUploadInputs()
        cleanUIInputs()
        enableUploadButton(uppy)

        if(pluginChooser) {
            pluginChooser.style.display = 'block'
        }
        if(isExternalUpload) {
            uploadButton.style.visibility = 'hidden'
        }
        lastFile = undefined
        if(uploadButton) {
            uploadButton.setAttribute("disabled", '1');
        }
        disableWarningOnPageUnload()
    });


    if(uploadButton) {
        uploadButton.addEventListener('click',  async function (e) {
            e.preventDefault()
            // document.querySelectorAll('.sidebar-button').forEach((value) => value.classList.remove('active'))
            // const tableMode = document.getElementById('table_mode') as HTMLInputElement;
            // tableMode.value = '';
            await prepareEncryptionKeysForUpload(uppy);
            await uppy.upload()
        })
    }

    uppy.on('dashboard:modal-closed', () => {
        const labelTo = document.getElementById('uppy-label-to') as HTMLLIElement
        if(labelTo) {
            const input = labelTo.querySelector('input') as HTMLInputElement
            labelTo.classList.add('d-none')  // todo only on inline
            labelTo.classList.remove('d-block')
            input.value = ''
        }
    })
    // uppy.on('dashboard:modal-open', (e) => {
    //     console.log('Modal is open', e)
    // })

    // }

    uppy.on('upload', () => {  // upload starts
        if(uploadButton) {
            uploadButton.setAttribute("disabled", '1');
        }
        if(isExternalUpload) {
            uploadButton.style.visibility = 'hidden'
        }
        disableUIUploadInputs()
        // const plugin = uppy.getPlugin("AwsS3Multipart")

        enableWarningOnPageUnload()
    });
    uppy.on('upload-error', () => {
        console.log('error')
        updateUIReset()
        enableUIUploadInputs()
        lastFile = undefined
    });
    uppy.on('reset', () => {
        updateUIReset()
        enableUIUploadInputs()

        cleanUIInputs()

        lastFile = undefined
        disableWarningOnPageUnload()
        if(uploadButton) {
            uploadButton.setAttribute("disabled", '1');
        }
        if(isWidget) {
            uploadOptions.style.display =' block'
        }
        if(isExternalUpload) {
            uploadOptions.style.display =' block'
        }
    })

    const chunkSize = 16 * 1024 * 1024
    // @ts-ignore
    uppy.use(AwsS3Multipart, {
        companionUrl: '/',
        limit: 4,
        companionHeaders: {'Cache-Control': 'no-store, max-age=0'},
        timeout: 300 * 1000,   // 5 mins?
        retryDelays: [0, 1000, 3000, 5000, 5000],  // one more retry
        getChunkSize() {
            return chunkSize
        },
        createMultipartUpload(file: any) {
            // const _self: any = this;
            const _self: any = uppy.getPlugin("AwsS3Multipart");
            _self.assertHost('createMultipartUpload');
            return _self.client.post('s3/multipart', {
                prefix: _self.crypto.prefix,
                name: (file as any).hashedFilename
            });
        },
        async prepareUploadParts(file: any, partData: any) {
            const _self: any = uppy.getPlugin("AwsS3Multipart");
            const algorithm = _self.crypto.algorithm;
            const key = _self.crypto.key

            // const ciphertext = await encrypt(partData.body, key, algorithm)

            // cleanup
            for (let chunkIndex =0; chunkIndex < _self.uploaders[file.id].chunkState.length; chunkIndex++) {
                const state = _self.uploaders[file.id].chunkState[chunkIndex]
                if (state.done && _self.uploaders[file.id].chunks[chunkIndex]) {
                    _self.uploaders[file.id].chunks[chunkIndex] = null
                }
            }
            // we have to set the data tight here...
            // _self.uploaders[file.id].chunks[partData.number - 1] = new Blob([ciphertext])

            const AesGCMSignatureLength = 16;  // the chunks are all 16 Bytes larger due to the signature length of the authenticated encryption
            partData.starts = []
            partData.ends = []
            for (let partNumber of partData.partNumbers) {
                const partNum = parseInt(partNumber) - 1
                let body = this.uploaders[file.id].chunks[partNum]
                const ciphertext = await encrypt(body, key, algorithm)
                // free
                _self.uploaders[file.id].chunks[partNum] = null;
                body = null;
                // todo: the following is erroneous if file size is larger than 10000*16MB = 160GB
                const start = partNum * (chunkSize + AesGCMSignatureLength)
                const end = start + Math.min((chunkSize + AesGCMSignatureLength), ciphertext.byteLength)
                partData.starts.push(start)
                partData.ends.push(end)
                this.uploaders[file.id].chunks[partNum] =  new Blob([new Uint8Array(ciphertext)]);
            }

            return _self.client.get(`s3/multipart/${partData['uploadId']}/batch?key=${partData.key}&partNumbers=${partData.partNumbers.join(',')}&starts=${partData.starts.join(',')}&ends=${partData.ends.join(',')}`)
        },
    });


    const uploadAgainButton = document.getElementById('upload-again-button');
    if(uploadAgainButton) {
        uploadAgainButton.addEventListener('click', event => {
            event.preventDefault();
            uppy.cancelAll();
            uppy.emit('reset');

            // sender-name, sender-email not cleared, because you might want to add this again?

        });
    }

    if (navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1) {
        document.getElementsByClassName('uppy-Dashboard-input')[0].setAttribute('webkitdirectory', 'webkitdirectory')
        document.getElementsByClassName('uppy-Dashboard-input')[0].setAttribute('directory', 'directory')
    }
}


export function setupPluginLink() {
    let x = document.getElementById('plugin-copy-link')
    if (x) {
        x.addEventListener('click', (e) => {
            e.preventDefault()
            const pluginLink = (document.getElementById('plugin-copy-link') as HTMLAnchorElement).href;

            window.parent.postMessage(pluginLink, '*')
        })
    }
}

document.addEventListener("DOMContentLoaded", function () {



});
